import httpClient from '@common/api/httpClient';
import httpNoAuthClient from '@common/api/httpNoAuthClient';
import { maxSuggestionsReturned } from '@common/api/rest/helpers';
import { type GeoCountry, type Language, type TimeZoneResult } from '@common/types/api';
import { type FilterSuggestion } from '@common/types/deviceFilters';
import type TimeZone from '@common/types/models/TimeZone';
import { createTimeZoneFromResponse } from '@common/types/models/TimeZone';

export async function fetchLanguages(): Promise<Record<string, string>> {
  try {
    const { data } = await httpNoAuthClient.get<Array<Language>>('/rest/v1/Languages');
    return data.reduce(
      (result, language) => ({
        ...result,
        [language.culture]: language.name,
      }),
      {},
    );
  } catch {
    return {};
  }
}

export async function fetchTimeZones(): Promise<TimeZone[]> {
  const { data = [] } = await httpNoAuthClient.get<TimeZoneResult[]>('/rest/v1/TimeZone');
  return data.map(createTimeZoneFromResponse);
}

export async function fetchCities(prefix: string): Promise<FilterSuggestion[]> {
  const { data } = await httpClient.get<string[]>(
    '/rest/v1/Geo/Cities',
    {
      params: {
        filter: prefix,
        take: maxSuggestionsReturned,
      },
    },
  );
  return data.toSorted((a, b) => a.localeCompare(b)).map((city) => ({
    id: city,
    value: city,
  }));
}

export async function fetchCountries(prefix: string): Promise<FilterSuggestion[]> {
  const { data } = await httpClient.get<GeoCountry[]>(
    '/rest/v1/Geo/Countries',
    {
      params: {
        filter: prefix,
        take: maxSuggestionsReturned,
      },
    },
  );
  return data.map((country) => ({
    id: country.code,
    value: country.name,
  })).toSorted(
    (optionA, optionB) => optionA.value.localeCompare(optionB.value),
  );
}

export async function fetchLocalizedCountries(
  culture: string,
  prefix?: string,
  take: number = maxSuggestionsReturned,
): Promise<FilterSuggestion[]> {
  const { data } = await httpNoAuthClient.get<GeoCountry[]>(
    `/rest/v1/Geo/Countries/${culture}`,
    {
      params: {
        filter: prefix,
        take,
      },
    },
  );
  return data.map((country) => ({
    id: country.code,
    value: country.name,
  })).toSorted(
    (optionA, optionB) => optionA.value.localeCompare(optionB.value),
  );
}

export async function fetchCountriesForUser(
  prefix: string,
  allowedCountryCodes?: string[],
): Promise<FilterSuggestion[]> {
  const allCountries = await fetchCountries(prefix);
  // If no country restrictions, return all countries
  if (!allowedCountryCodes || allowedCountryCodes.length === 0) {
    return allCountries;
  }

  // Filter countries to only those the user has access to
  return allCountries.filter((country) => allowedCountryCodes.includes(country.id));
}
